export class Reconnector {
  private attempts = 0;
  private maxAttempts: number;
  private baseDelay: number;
  private maxDelay: number;
  private timeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor(
    maxAttempts = Infinity,
    baseDelay = 1000,
    maxDelay = 30000
  ) {
    this.maxAttempts = maxAttempts;
    this.baseDelay = baseDelay;
    this.maxDelay = maxDelay;
  }

  /**
   * Schedule a function to run after an exponential backoff delay
   * Returns a promise that resolves when the function completes successfully
   * or rejects if the function throws an error
   */
  async schedule(fn: () => Promise<void>): Promise<void> {
    if (this.attempts >= this.maxAttempts) {
      throw new Error('Max reconnection attempts reached');
    }

    const delay = this.calculateDelay();

    return new Promise((resolve, reject) => {
      this.timeoutId = setTimeout(async () => {
        try {
          await fn();
          this.reset(); // Success, reset attempts
          resolve();
        } catch (error) {
          this.attempts++;
          reject(error);
        }
      }, delay);
    });
  }

  /**
   * Calculate delay with exponential backoff and jitter
   * Formula: min(baseDelay * 2^attempts, maxDelay) ± 25% jitter
   */
  private calculateDelay(): number {
    // Exponential backoff: 1s, 2s, 4s, 8s, 16s, 30s (capped)
    const exponential = this.baseDelay * Math.pow(2, this.attempts);
    const capped = Math.min(exponential, this.maxDelay);

    // Add jitter: ±25% randomization
    const jitter = capped * 0.25 * (Math.random() * 2 - 1);

    return Math.round(capped + jitter);
  }

  /**
   * Reset the attempts counter and cancel any pending timeout
   */
  reset(): void {
    this.attempts = 0;
    this.cancel();
  }

  /**
   * Cancel any pending reconnection attempt
   */
  cancel(): void {
    if (this.timeoutId !== null) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  /**
   * Get the current number of attempts
   */
  getAttempts(): number {
    return this.attempts;
  }
}
